import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutTemplate, Image as ImageIcon, Upload, Plus,
  Trash, Pencil, Save, X, Check, AlertCircle, Loader2,
  Share2, Phone, Mail, MapPin, Facebook, Instagram,
  Linkedin, Youtube, Twitter, Send, MessageCircle,
  Activity, Calendar, Clock, Map, Users, Briefcase, ToggleLeft, ToggleRight
} from 'lucide-react';
// FIX: FORM_POST imported for Multipart/Form-data handling
import { GET, POST, PUT, DELETE, FORM_POST } from '../services/api';

// --- Global Styles ---
const CustomStyles = () => (
  <style>{`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
    .animate-fade-in-up { animation: fade-in-up 0.4s ease-out forwards; }
    @keyframes fade-in-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  `}</style>
);

// --- Shared Components ---
const Toast = ({ message, type, onClose }) => {
  if (!message) return null;
  const bgColor = type === 'success' ? 'bg-emerald-600' : type === 'error' ? 'bg-red-500' : 'bg-blue-600';
  return (
    <div className={`fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-2xl text-white font-bold flex items-center gap-3 z-[1000] animate-in slide-in-from-bottom-5 ${bgColor}`}>
      {type === 'success' ? <Check className="w-5 h-5" /> : type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
      {message}
      <button onClick={onClose} className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors"><X className="w-4 h-4" /></button>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[999] p-4 animate-in fade-in">
      <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-sm w-full text-center border border-slate-100 scale-100 animate-in zoom-in-95">
        <div className="w-14 h-14 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-red-50">
          <Trash className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-2">{title}</h3>
        <p className="text-slate-500 mb-6 font-medium">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold transition-colors">Cancel</button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-bold transition-colors shadow-lg shadow-red-200">Confirm</button>
        </div>
      </div>
    </div>
  );
};

// --- Helper: Icon Mapper for Social Links ---
const getSocialIcon = (type) => {
  switch (type) {
    case 'phone': return <Phone className="w-5 h-5" />;
    case 'whatsapp': return <MessageCircle className="w-5 h-5" />;
    case 'telegram': return <Send className="w-5 h-5" />;
    case 'instagram': return <Instagram className="w-5 h-5" />;
    case 'facebook': return <Facebook className="w-5 h-5" />;
    case 'linkedin': return <Linkedin className="w-5 h-5" />;
    case 'twitter': return <Twitter className="w-5 h-5" />;
    case 'youtube': return <Youtube className="w-5 h-5" />;
    case 'email': return <Mail className="w-5 h-5" />;
    case 'address': return <MapPin className="w-5 h-5" />;
    default: return <Share2 className="w-5 h-5" />;
  }
};

// --- Helper: Color Mapper for Social Badges ---
const getSocialColor = (type) => {
  const colors = {
    phone: 'text-emerald-600 bg-emerald-50 border-emerald-100',
    whatsapp: 'text-green-600 bg-green-50 border-green-100',
    instagram: 'text-pink-600 bg-pink-50 border-pink-100',
    facebook: 'text-blue-600 bg-blue-50 border-blue-100',
    linkedin: 'text-sky-700 bg-sky-50 border-sky-100',
    youtube: 'text-red-600 bg-red-50 border-red-100',
    email: 'text-amber-600 bg-amber-50 border-amber-100',
    twitter: 'text-slate-700 bg-slate-100 border-slate-200',
    address: 'text-slate-600 bg-slate-100 border-slate-200',
  };
  return colors[type] || 'text-blue-600 bg-blue-50 border-blue-100';
};

// =================================================================
// MAIN COMPONENT
// =================================================================
const LandingPageManagement = () => {
  const [activeTab, setActiveTab] = useState('hero');
  const [toast, setToast] = useState({ message: '', type: '', id: null });
  const [confirmation, setConfirmation] = useState({ isOpen: false, title: '', message: '', onConfirm: () => { }, onCancel: () => { } });

  // --- Hero Section State ---
  const [slides, setSlides] = useState([]);
  const [loadingSlides, setLoadingSlides] = useState(false);
  const [isSlideModalOpen, setIsSlideModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [slidePreview, setSlidePreview] = useState('');
  const [slideForm, setSlideForm] = useState({ heading: '', subHeading: '', imageFile: null });
  const [slideErrors, setSlideErrors] = useState({});
  const [isSubmittingSlide, setIsSubmittingSlide] = useState(false);

  // --- Social Links State ---
  const [socialLinks, setSocialLinks] = useState([]);
  const [loadingSocial, setLoadingSocial] = useState(false);
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const [editingSocial, setEditingSocial] = useState(null);
  const [socialForm, setSocialForm] = useState({ name: '', link: '', type: '' });
  const [isSubmittingSocial, setIsSubmittingSocial] = useState(false);

  // --- Activity Highlights State ---
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityForm, setActivityForm] = useState({
    tag: '',
    title: '',
    description: '',
    existingImages: [],
    newImages: []
  });
  const [isSubmittingActivity, setIsSubmittingActivity] = useState(false);

  // --- Our Chapters (Networks) State (NEW) ---
  const [chapters, setChapters] = useState([]);
  const [cities, setCities] = useState([]); // For dropdown
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [isChapterModalOpen, setIsChapterModalOpen] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [chapterForm, setChapterForm] = useState({
    city_id: '',
    description: '',
    meeting: { day: 'Thursday', startTime: '', endTime: '' },
    highlight: { title: '', description: '' },
    isActive: true
  });
  const [isSubmittingChapter, setIsSubmittingChapter] = useState(false);

  const ALL_SOCIAL_TYPES = ['phone', 'whatsapp', 'telegram', 'instagram', 'facebook', 'linkedin', 'twitter', 'youtube', 'email', 'address'];

  // --- Helpers ---
  const showToast = useCallback((message, type = 'success') => {
    if (toast.id) clearTimeout(toast.id);
    const newId = setTimeout(() => setToast({ message: '', type: '', id: null }), 3000);
    setToast({ message, type, id: newId });
  }, [toast.id]);

  // --- API Calls ---
  const fetchSlides = useCallback(async () => {
    setLoadingSlides(true);
    try {
      const response = await GET('/hero-section');
      if (response && response.success) setSlides(response.data || []);
    } catch (error) {
      console.error("Error fetching slides:", error);
      showToast('Failed to load hero slides', 'error');
    } finally { setLoadingSlides(false); }
  }, [showToast]);

  const fetchSocialLinks = useCallback(async () => {
    setLoadingSocial(true);
    try {
      const response = await GET('/landing/social-links/');
      if (response && response.success) setSocialLinks(response.data || []);
    } catch (error) {
      console.error("Error fetching social links:", error);
      showToast('Failed to load social links', 'error');
    } finally { setLoadingSocial(false); }
  }, [showToast]);

  const fetchActivities = useCallback(async () => {
    setLoadingActivities(true);
    try {
      const response = await GET('/landing/activity');
      if (response && response.success) setActivities(response.data || []);
    } catch (error) {
      console.error("Error fetching activities:", error);
      showToast('Failed to load activities', 'error');
    } finally { setLoadingActivities(false); }
  }, [showToast]);

  // NEW: Fetch Chapters and Cities
  const fetchCities = useCallback(async () => {
    try {
      const response = await GET('/landing/cities/');
      if (response) setCities(response || []); // API returns array directly based on prompt
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  }, []);

  const fetchChapters = useCallback(async () => {
    setLoadingChapters(true);
    try {
      const response = await GET('/landing/networks');
      if (response && response.success) setChapters(response.data || []);
    } catch (error) {
      console.error("Error fetching chapters:", error);
      showToast('Failed to load chapters', 'error');
    } finally { setLoadingChapters(false); }
  }, [showToast]);

  useEffect(() => {
    if (activeTab === 'hero') fetchSlides();
    if (activeTab === 'social') fetchSocialLinks();
    if (activeTab === 'activity') fetchActivities();
    if (activeTab === 'chapters') {
      fetchChapters();
      fetchCities();
    }
  }, [activeTab, fetchSlides, fetchSocialLinks, fetchActivities, fetchChapters, fetchCities]);

  // --- Hero Handlers ---
  const handleSlideImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { showToast('Image size < 5MB', 'error'); return; }
      setSlideForm(prev => ({ ...prev, imageFile: file }));
      setSlidePreview(URL.createObjectURL(file));
      if (slideErrors.image) setSlideErrors(prev => ({ ...prev, image: '' }));
    }
  };

  const openSlideModal = (slide = null) => {
    if (slide) {
      setEditingSlide(slide);
      setSlideForm({ 
        heading: slide.heading, 
        subHeading: slide.sub_heading || slide.subHeading || '', 
        imageFile: null 
      });
      setSlidePreview(slide.hero_image);
    } else {
      setEditingSlide(null);
      setSlideForm({ heading: '', subHeading: '', imageFile: null });
      setSlidePreview('');
    }
    setIsSlideModalOpen(true);
    setSlideErrors({});
  };

  const handleSlideSubmit = async () => {
    const errors = {};
    if (!slideForm.heading.trim()) errors.heading = 'Required';
    if (!slideForm.subHeading.trim()) errors.subHeading = 'Required';
    if (!editingSlide && !slideForm.imageFile) errors.image = 'Required';
    setSlideErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSubmittingSlide(true);
    try {
      const apiData = { 
        heading: slideForm.heading, 
        subHeading: slideForm.subHeading, 
        hero_image: slideForm.imageFile 
      };

      const result = await FORM_POST(
        editingSlide ? `/hero-section/${editingSlide._id}` : '/hero-section',
        apiData,
        editingSlide ? 'PUT' : 'POST'
      );

      if (result && result.success) {
        showToast(editingSlide ? 'Slide updated' : 'Slide added', 'success');
        setIsSlideModalOpen(false);
        fetchSlides();
      } else {
        throw new Error(result.message || 'Operation failed');
      }
    } catch (error) { 
      showToast(error.message || 'Error saving slide', 'error'); 
    } finally { 
      setIsSubmittingSlide(false); 
    }
  };

  const handleDeleteSlide = (id) => {
    setConfirmation({
      isOpen: true, title: "Delete Slide", message: "Are you sure?",
      onConfirm: async () => {
        try {
          const res = await DELETE(`/hero-section/${id}`);
          if (res.success) { showToast("Slide deleted", "success"); fetchSlides(); }
        } catch (e) { showToast("Failed to delete", "error"); }
        setConfirmation(prev => ({ ...prev, isOpen: false }));
      },
      onCancel: () => setConfirmation(prev => ({ ...prev, isOpen: false }))
    });
  };

  // --- Social Link Handlers ---
  const openSocialModal = (link = null) => {
    if (link) {
      setEditingSocial(link);
      setSocialForm({ name: link.name, link: link.link, type: link.type });
    } else {
      setEditingSocial(null);
      setSocialForm({ name: '', link: '', type: '' });
    }
    setIsSocialModalOpen(true);
  };

  const handleSocialSubmit = async () => {
    if (!socialForm.name || !socialForm.link || !socialForm.type) {
      showToast('Please fill all fields', 'error'); return;
    }

    setIsSubmittingSocial(true);
    try {
      const endpoint = editingSocial ? `/landing/social-links/${editingSocial._id}` : '/landing/social-links/';
      const result = await (editingSocial 
        ? PUT(endpoint, socialForm) 
        : POST(endpoint, socialForm)
      );

      if (result && result.success) {
        showToast(editingSocial ? 'Link updated' : 'Link added', 'success');
        setIsSocialModalOpen(false);
        setSocialForm({ name: '', link: '', type: '' });
        fetchSocialLinks();
      } else {
        throw new Error(result?.message || 'Operation failed');
      }
    } catch (error) {
      showToast(error.message || 'Error saving link', 'error');
    } finally { 
      setIsSubmittingSocial(false);
    }
  };

  const handleDeleteSocial = (id) => {
    setConfirmation({
      isOpen: true, title: "Delete Link", message: "Remove this social link?",
      onConfirm: async () => {
        try {
          const res = await DELETE(`/landing/social-links/${id}`);
          if (res.success) { showToast("Link deleted", "success"); fetchSocialLinks(); }
        } catch (e) { showToast("Failed to delete", "error"); }
        setConfirmation(prev => ({ ...prev, isOpen: false }));
      },
      onCancel: () => setConfirmation(prev => ({ ...prev, isOpen: false }))
    });
  };

  const getAvailableSocialTypes = () => {
    const usedTypes = socialLinks.map(l => l.type);
    return ALL_SOCIAL_TYPES.filter(type => 
      !usedTypes.includes(type) || (editingSocial && editingSocial.type === type)
    );
  };

  const isAllTypesUsed = getAvailableSocialTypes().length === 0 && !editingSocial;

  // --- Activity Highlights Handlers ---

  const openActivityModal = (activity = null) => {
    if (activity) {
      setEditingActivity(activity);
      setActivityForm({
        tag: activity.tag || '',
        title: activity.title || '',
        description: activity.description || '',
        existingImages: activity.images || [],
        newImages: []
      });
    } else {
      setEditingActivity(null);
      setActivityForm({
        tag: '',
        title: '',
        description: '',
        existingImages: [],
        newImages: []
      });
    }
    setIsActivityModalOpen(true);
  };

  const handleActivityImageChange = (e) => {
    const files = Array.from(e.target.files);
    const currentCount = activityForm.existingImages.length + activityForm.newImages.length;
    
    if (currentCount + files.length > 3) {
      showToast('Maximum 3 images allowed', 'error');
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        showToast(`Skipped ${file.name}: > 5MB`, 'error');
        return false;
      }
      return true;
    });

    setActivityForm(prev => ({
      ...prev,
      newImages: [...prev.newImages, ...validFiles]
    }));
  };

  const removeActivityImage = (index, type) => {
    if (type === 'existing') {
      setActivityForm(prev => ({
        ...prev,
        existingImages: prev.existingImages.filter((_, i) => i !== index)
      }));
    } else {
      setActivityForm(prev => ({
        ...prev,
        newImages: prev.newImages.filter((_, i) => i !== index)
      }));
    }
  };

  const handleActivitySubmit = async () => {
    if (!activityForm.tag.trim()) { showToast('Tag is required', 'error'); return; }
    if (!activityForm.title.trim()) { showToast('Title is required', 'error'); return; }
    if (!activityForm.description.trim()) { showToast('Description is required', 'error'); return; }

    const totalImages = activityForm.existingImages.length + activityForm.newImages.length;
    if (totalImages < 1) { showToast('Minimum 1 image required', 'error'); return; }
    if (totalImages > 3) { showToast('Maximum 3 images allowed', 'error'); return; }

    setIsSubmittingActivity(true);
    try {
      const formData = new FormData();
      formData.append('tag', activityForm.tag);
      formData.append('title', activityForm.title);
      formData.append('description', activityForm.description);
      
      activityForm.newImages.forEach((file) => {
        formData.append('activity_image', file);
      });

      if (activityForm.existingImages.length > 0) {
          activityForm.existingImages.forEach(url => formData.append('existing_images', url)); 
      }

      const endpoint = editingActivity ? `/landing/activity/${editingActivity._id}` : '/landing/activity';
      const method = editingActivity ? 'PUT' : 'POST';
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:9000/api'}${endpoint}`, {
         method: method,
         headers: { 'Authorization': `Bearer ${token}` },
         body: formData
      });
      
      const result = await response.json();

      if (result.success) {
        showToast(editingActivity ? 'Activity updated' : 'Activity added', 'success');
        setIsActivityModalOpen(false);
        fetchActivities();
      } else {
        throw new Error(result.message || 'Operation failed');
      }
    } catch (error) {
      showToast(error.message || 'Error saving activity', 'error');
    } finally {
      setIsSubmittingActivity(false);
    }
  };

  const handleDeleteActivity = (id) => {
    setConfirmation({
      isOpen: true, title: "Delete Activity", message: "Permanently delete this activity?",
      onConfirm: async () => {
        try {
          const res = await DELETE(`/landing/activity/${id}`);
          if (res.success) { showToast("Activity deleted", "success"); fetchActivities(); }
        } catch (e) { showToast("Failed to delete", "error"); }
        setConfirmation(prev => ({ ...prev, isOpen: false }));
      },
      onCancel: () => setConfirmation(prev => ({ ...prev, isOpen: false }))
    });
  };

  // --- Our Chapters (Networks) Handlers (NEW) ---

  const openChapterModal = (chapter = null) => {
    if (chapter) {
      setEditingChapter(chapter);
      setChapterForm({
        city_id: chapter.city?._id || chapter.city_id || '',
        description: chapter.description || '',
        meeting: {
          day: chapter.meeting?.day || 'Thursday',
          startTime: chapter.meeting?.startTime || '',
          endTime: chapter.meeting?.endTime || ''
        },
        highlight: {
          title: chapter.highlight?.title || '',
          description: chapter.highlight?.description || ''
        },
        isActive: chapter.isActive ?? true
      });
    } else {
      setEditingChapter(null);
      setChapterForm({
        city_id: '',
        description: '',
        meeting: { day: 'Thursday', startTime: '', endTime: '' },
        highlight: { title: '', description: '' },
        isActive: true
      });
    }
    setIsChapterModalOpen(true);
  };

  const handleChapterSubmit = async () => {
    if (!chapterForm.city_id) { showToast('City is required', 'error'); return; }
    if (!chapterForm.description) { showToast('Description is required', 'error'); return; }
    if (!chapterForm.meeting.startTime || !chapterForm.meeting.endTime) { showToast('Meeting times are required', 'error'); return; }
    if (!chapterForm.highlight.title || !chapterForm.highlight.description) { showToast('Highlights are required', 'error'); return; }

    setIsSubmittingChapter(true);
    try {
      const endpoint = editingChapter ? `/landing/networks/${editingChapter._id}` : '/landing/networks';
      
      // Construct payload according to JSON spec
      const payload = {
        city_id: chapterForm.city_id,
        description: chapterForm.description,
        meeting: chapterForm.meeting,
        highlight: chapterForm.highlight,
        isActive: chapterForm.isActive
      };

      const result = await (editingChapter 
        ? PUT(endpoint, payload) 
        : POST(endpoint, payload)
      );

      if (result && (result.success || result.code === 200 || result.code === 201)) {
        showToast(editingChapter ? 'Chapter updated' : 'Chapter added', 'success');
        setIsChapterModalOpen(false);
        fetchChapters();
      } else {
        throw new Error(result?.message || 'Operation failed');
      }
    } catch (error) {
      showToast(error.message || 'Error saving chapter', 'error');
    } finally {
      setIsSubmittingChapter(false);
    }
  };

  const handleDeleteChapter = (id) => {
    setConfirmation({
      isOpen: true, title: "Delete Chapter", message: "Permanently delete this chapter?",
      onConfirm: async () => {
        try {
          const res = await DELETE(`/landing/networks/${id}`);
          if (res.success || res.code === 200) { showToast("Chapter deleted", "success"); fetchChapters(); }
        } catch (e) { showToast("Failed to delete", "error"); }
        setConfirmation(prev => ({ ...prev, isOpen: false }));
      },
      onCancel: () => setConfirmation(prev => ({ ...prev, isOpen: false }))
    });
  };

  // --- Render ---
  const navItems = [
    { id: 'hero', label: 'Hero Section', icon: LayoutTemplate },
    { id: 'social', label: 'Social Links', icon: Share2 },
    { id: 'activity', label: 'Activity Highlights', icon: Activity },
    { id: 'chapters', label: 'Our Chapters', icon: Map }, // New Tab
  ];

  return (
    <div>
      <CustomStyles />
      <div className="space-y-5">
        
        {/* Navigation Tabs */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 mb-6 p-2 overflow-x-auto custom-scrollbar">
          <nav className="flex space-x-1 min-w-max">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`px-4 py-3 rounded-2xl text-sm font-bold flex items-center space-x-2 transition-all ${
                  activeTab === item.id 
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' 
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* --- HERO SECTION TAB --- */}
        {activeTab === 'hero' && (
          <div className="animate-fade-in-up">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6 flex justify-between items-center">
               <div className="flex items-center gap-3">
                 <div className="p-2 bg-blue-50 rounded-xl text-blue-600"><LayoutTemplate className="w-6 h-6" /></div>
                 <div><h1 className="text-xl font-bold text-slate-800">Hero Section</h1><p className="text-sm text-slate-500">Manage home page sliders</p></div>
               </div>
               <div className="flex items-center gap-3">
                 <span className={`px-3 py-1 rounded-lg text-xs font-bold ${slides.length >= 4 ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-600'}`}>{slides.length} / 4 Used</span>
                 <button onClick={() => openSlideModal()} disabled={slides.length >= 4} className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg transition-all ${slides.length >= 4 ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200'}`}><Plus className="w-4 h-4"/> Add Slide</button>
               </div>
            </div>

            {loadingSlides ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>
            ) : slides.length === 0 ? (
              <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-12 text-center">
                <h3 className="text-lg font-bold text-slate-800 mb-2">No Slides Found</h3>
                <button onClick={() => openSlideModal()} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold">Create First Slide</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {slides.map((slide) => (
                  <div key={slide._id} className="group bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:border-blue-200 transition-all flex flex-col">
                    <div className="relative h-48 w-full bg-slate-100">
                      <img src={slide.hero_image} alt="Hero" className="w-full h-full object-cover" />
                      <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openSlideModal(slide)} className="p-2 bg-white/90 text-amber-600 rounded-lg shadow-sm hover:scale-105 transition-transform"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteSlide(slide._id)} className="p-2 bg-white/90 text-red-600 rounded-lg shadow-sm hover:scale-105 transition-transform"><Trash className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="p-5 flex-1">
                      <h3 className="font-bold text-slate-800 line-clamp-1 mb-1">{slide.heading}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2">{slide.sub_heading || slide.subHeading}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* --- SOCIAL LINKS TAB --- */}
        {activeTab === 'social' && (
          <div className="animate-fade-in-up">
             <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6 flex justify-between items-center">
               <div className="flex items-center gap-3">
                 <div className="p-2 bg-emerald-50 rounded-xl text-emerald-600"><Share2 className="w-6 h-6" /></div>
                 <div><h1 className="text-xl font-bold text-slate-800">Social Links</h1><p className="text-sm text-slate-500">Manage contact & social media links</p></div>
               </div>
               
               <button 
                  onClick={() => openSocialModal()} 
                  disabled={isAllTypesUsed}
                  className={`px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg transition-all ${
                    isAllTypesUsed 
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none' 
                      : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-200'
                  }`}
               >
                 <Plus className="w-4 h-4"/> Add Link
               </button>
            </div>

            {loadingSocial ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-600 animate-spin" /></div>
            ) : socialLinks.length === 0 ? (
              <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-12 text-center">
                <h3 className="text-lg font-bold text-slate-800 mb-2">No Social Links Added</h3>
                <button onClick={() => openSocialModal()} className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-bold">Add First Link</button>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50">
                      <tr>
                        {['Type', 'Name', 'Link / Value', 'Status', 'Actions'].map(h => <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {socialLinks.map(link => (
                        <tr key={link._id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-5 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border uppercase ${getSocialColor(link.type)}`}>
                              {getSocialIcon(link.type)}
                              {link.type}
                            </span>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm font-bold text-slate-800">{link.name}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600 max-w-xs truncate">{link.link}</td>
                          <td className="px-5 py-3 whitespace-nowrap"><span className="inline-flex px-2 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-100">Active</span></td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm flex gap-2">
                            <button onClick={() => openSocialModal(link)} className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => handleDeleteSocial(link._id)} className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"><Trash className="w-4 h-4" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* --- ACTIVITY HIGHLIGHTS TAB --- */}
        {activeTab === 'activity' && (
          <div className="animate-fade-in-up">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6 flex justify-between items-center">
               <div className="flex items-center gap-3">
                 <div className="p-2 bg-blue-50 rounded-xl text-blue-600"><Activity className="w-6 h-6" /></div>
                 <div><h1 className="text-xl font-bold text-slate-800">Activity Highlights</h1><p className="text-sm text-slate-500">Share recent events & updates</p></div>
               </div>
               <button onClick={() => openActivityModal()} className="px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200">
                 <Plus className="w-4 h-4"/> Add Activity
               </button>
            </div>

            {loadingActivities ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>
            ) : activities.length === 0 ? (
              <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-12 text-center">
                <h3 className="text-lg font-bold text-slate-800 mb-2">No Activities Found</h3>
                <button onClick={() => openActivityModal()} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold">Post First Activity</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {activities.map((item) => (
                  <div key={item._id} className="group bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden hover:border-blue-200 hover:shadow-md transition-all flex flex-col h-full">
                    <div className="relative h-56 bg-slate-100 overflow-hidden">
                      {item.images && item.images.length > 0 ? (
                        <div className="w-full h-full flex">
                           <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover" />
                           {item.images.length > 1 && (
                             <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-bold px-2 py-1 rounded-lg backdrop-blur-sm">
                               + {item.images.length - 1} more
                             </div>
                           )}
                        </div>
                      ) : (
                         <div className="w-full h-full flex items-center justify-center text-slate-400">
                           <ImageIcon className="w-10 h-10" />
                         </div>
                      )}
                      
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-blue-700 text-xs font-extrabold uppercase tracking-wide rounded-lg shadow-sm">
                          {item.tag}
                        </span>
                      </div>

                      <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
                        <button onClick={() => openActivityModal(item)} className="p-2 bg-white text-blue-600 rounded-xl shadow-lg hover:bg-blue-50 transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteActivity(item._id)} className="p-2 bg-white text-red-600 rounded-xl shadow-lg hover:bg-red-50 transition-colors"><Trash className="w-4 h-4" /></button>
                      </div>
                    </div>

                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-slate-400 text-xs font-bold mb-3 uppercase tracking-wider">
                         <Calendar className="w-3.5 h-3.5" />
                         <span>{new Date(item.activityDate).toLocaleDateString()}</span>
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 mb-2 line-clamp-2 leading-tight">{item.title}</h3>
                      <p className="text-slate-500 text-sm line-clamp-3 mb-4 flex-1">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* --- OUR CHAPTERS TAB (NEW) --- */}
        {activeTab === 'chapters' && (
          <div className="animate-fade-in-up">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6 flex justify-between items-center">
               <div className="flex items-center gap-3">
                 <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600"><Map className="w-6 h-6" /></div>
                 <div><h1 className="text-xl font-bold text-slate-800">Our Chapters</h1><p className="text-sm text-slate-500">Manage network presence per city</p></div>
               </div>
               <button onClick={() => openChapterModal()} className="px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg transition-all bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-200">
                 <Plus className="w-4 h-4"/> Add Chapter
               </button>
            </div>

            {loadingChapters ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>
            ) : chapters.length === 0 ? (
              <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-12 text-center">
                <h3 className="text-lg font-bold text-slate-800 mb-2">No Chapters Found</h3>
                <button onClick={() => openChapterModal()} className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold">Add First Chapter</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {chapters.map((chapter) => (
                  <div key={chapter._id} className="bg-white rounded-3xl shadow-sm border border-slate-200 hover:border-indigo-200 transition-all p-6 relative group">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-lg">
                                {chapter.city?.name ? chapter.city.name.substring(0, 2).toUpperCase() : 'NA'}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">{chapter.city?.name || 'Unknown City'}</h3>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${chapter.isActive ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                        {chapter.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openChapterModal(chapter)} className="p-2 bg-slate-50 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => handleDeleteChapter(chapter._id)} className="p-2 bg-slate-50 text-red-600 rounded-lg hover:bg-red-50 transition-colors"><Trash className="w-4 h-4" /></button>
                        </div>
                    </div>

                    <p className="text-slate-600 text-sm mb-6 line-clamp-2">{chapter.description}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                         <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                             <div className="flex items-center gap-2 mb-2 text-slate-400 text-xs font-bold uppercase">
                                 <Clock className="w-3.5 h-3.5" /> Meeting Schedule
                             </div>
                             <p className="text-sm font-bold text-slate-700">{chapter.meeting?.day || 'N/A'}</p>
                             <p className="text-xs text-slate-500">{chapter.meeting?.startTime} - {chapter.meeting?.endTime}</p>
                         </div>
                         <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100">
                             <div className="flex items-center gap-2 mb-2 text-amber-500 text-xs font-bold uppercase">
                                 <Briefcase className="w-3.5 h-3.5" /> {chapter.highlight?.title || 'Highlight'}
                             </div>
                             <p className="text-xs text-slate-600 line-clamp-2">{chapter.highlight?.description}</p>
                         </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-xs text-slate-500 font-bold">
                        <div className="flex items-center gap-1.5"><Map className="w-4 h-4 text-slate-400" /> {chapter.totalChapters || 0} Chapters</div>
                        <div className="flex items-center gap-1.5"><Users className="w-4 h-4 text-slate-400" /> {chapter.totalActiveMembers || 0} Members</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- MODAL: HERO SLIDE --- */}
      {isSlideModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold text-slate-800">{editingSlide ? 'Edit Slide' : 'Add Slide'}</h3>
              <button onClick={() => setIsSlideModalOpen(false)}><X className="w-5 h-5 text-slate-400 hover:text-slate-600" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase">Image *</label>
                <div className="relative border-2 border-dashed rounded-2xl p-4 flex flex-col items-center bg-slate-50 hover:bg-slate-100 transition-colors">
                  {slidePreview ? (
                    <div className="relative w-full h-40 rounded-xl overflow-hidden group">
                      <img src={slidePreview} alt="Preview" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <label className="cursor-pointer px-4 py-2 bg-white/20 text-white rounded-lg text-sm font-bold hover:bg-white/30 transition-colors">
                          Change <input type="file" accept="image/*" className="hidden" onChange={handleSlideImageChange} />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label className="cursor-pointer w-full h-32 flex flex-col items-center justify-center">
                      <div className="p-3 bg-white rounded-full shadow-sm mb-3"><Upload className="w-5 h-5 text-blue-500" /></div>
                      <p className="text-sm font-bold text-slate-700">Click to upload</p>
                      <input type="file" accept="image/*" className="hidden" onChange={handleSlideImageChange} />
                    </label>
                  )}
                </div>
                {slideErrors.image && <p className="text-xs font-bold text-red-500">{slideErrors.image}</p>}
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Heading *</label>
                  <input type="text" value={slideForm.heading} onChange={(e) => setSlideForm({...slideForm, heading: e.target.value})} className={`w-full p-3 bg-slate-50 border rounded-xl outline-none font-medium ${slideErrors.heading ? 'border-red-500' : 'border-slate-200 focus:ring-2 focus:ring-blue-500'}`} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Sub Heading *</label>
                  <textarea value={slideForm.subHeading} onChange={(e) => setSlideForm({...slideForm, subHeading: e.target.value})} rows="3" className={`w-full p-3 bg-slate-50 border rounded-xl outline-none font-medium ${slideErrors.subHeading ? 'border-red-500' : 'border-slate-200 focus:ring-2 focus:ring-blue-500'}`} />
                </div>
              </div>
            </div>
            <div className="p-6 border-t flex gap-3 bg-slate-50/50">
              <button onClick={() => setIsSlideModalOpen(false)} className="flex-1 px-4 py-3 border rounded-xl font-bold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button onClick={handleSlideSubmit} disabled={isSubmittingSlide} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-700 shadow-lg shadow-blue-200 disabled:opacity-70">
                {isSubmittingSlide ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: SOCIAL LINK --- */}
      {isSocialModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b flex justify-between items-center bg-white rounded-t-3xl">
              <h3 className="text-lg font-bold text-slate-800">{editingSocial ? 'Edit Link' : 'Add Link'}</h3>
              <button onClick={() => setIsSocialModalOpen(false)}><X className="w-5 h-5 text-slate-400 hover:text-slate-600" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Type *</label>
                <div className="relative">
                  <select 
                    value={socialForm.type} 
                    onChange={(e) => setSocialForm({...socialForm, type: e.target.value})} 
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-medium appearance-none text-slate-700 focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select Type</option>
                    {getAvailableSocialTypes().map(type => (
                      <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-3.5 pointer-events-none text-slate-400"><Share2 className="w-4 h-4"/></div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Label / Name *</label>
                <input 
                  type="text" 
                  value={socialForm.name} 
                  onChange={(e) => setSocialForm({...socialForm, name: e.target.value})} 
                  placeholder="e.g. Official WhatsApp"
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-medium focus:ring-2 focus:ring-emerald-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Value / Link *</label>
                <input 
                  type="text" 
                  value={socialForm.link} 
                  onChange={(e) => setSocialForm({...socialForm, link: e.target.value})} 
                  placeholder={socialForm.type === 'phone' ? 'e.g. +91 9876543210' : 'https://...'}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-medium focus:ring-2 focus:ring-emerald-500" 
                />
              </div>
            </div>
            <div className="p-6 border-t flex gap-3 bg-slate-50/50 rounded-b-3xl">
              <button onClick={() => setIsSocialModalOpen(false)} className="flex-1 px-4 py-3 border rounded-xl font-bold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button onClick={handleSocialSubmit} disabled={isSubmittingSocial} className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 shadow-lg shadow-emerald-200 disabled:opacity-70">
                {isSubmittingSocial ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: ACTIVITY HIGHLIGHTS --- */}
      {isActivityModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold text-slate-800">{editingActivity ? 'Edit Activity' : 'Post New Activity'}</h3>
              <button onClick={() => setIsActivityModalOpen(false)}><X className="w-5 h-5 text-slate-400 hover:text-slate-600" /></button>
            </div>
            
            <div className="p-6 space-y-6">
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                 <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Tag *</label>
                    <input 
                      type="text" 
                      value={activityForm.tag} 
                      onChange={(e) => setActivityForm({...activityForm, tag: e.target.value})} 
                      placeholder="e.g. Recent Activity"
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-medium focus:ring-2 focus:ring-blue-500" 
                    />
                 </div>
                 <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Title *</label>
                    <input 
                      type="text" 
                      value={activityForm.title} 
                      onChange={(e) => setActivityForm({...activityForm, title: e.target.value})} 
                      placeholder="Event Title"
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-medium focus:ring-2 focus:ring-blue-500" 
                    />
                 </div>
               </div>

               <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Description *</label>
                  <textarea 
                    rows="3"
                    value={activityForm.description} 
                    onChange={(e) => setActivityForm({...activityForm, description: e.target.value})} 
                    placeholder="Short description of the activity..."
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-medium focus:ring-2 focus:ring-blue-500" 
                  />
               </div>

               <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs font-bold text-slate-500 uppercase">Images (Min 1, Max 3) *</label>
                    <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                      {activityForm.existingImages.length + activityForm.newImages.length} / 3
                    </span>
                  </div>
                  
                  <div className="border-2 border-dashed border-slate-200 rounded-2xl p-4 bg-slate-50">
                    <div className="flex gap-4 overflow-x-auto pb-2 custom-scrollbar">
                       
                       {(activityForm.existingImages.length + activityForm.newImages.length) < 3 && (
                         <label className="flex-shrink-0 w-24 h-24 bg-white border-2 border-dashed border-blue-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50 hover:border-blue-400 transition-colors">
                            <Upload className="w-6 h-6 text-blue-500 mb-1" />
                            <span className="text-[10px] font-bold text-blue-600 uppercase">Upload</span>
                            <input 
                              type="file" 
                              multiple 
                              accept="image/*" 
                              className="hidden" 
                              onChange={handleActivityImageChange} 
                            />
                         </label>
                       )}

                       {activityForm.existingImages.map((url, index) => (
                         <div key={`existing-${index}`} className="flex-shrink-0 w-24 h-24 relative rounded-xl overflow-hidden shadow-sm border border-slate-200 group">
                           <img src={url} alt="Existing" className="w-full h-full object-cover" />
                           <button 
                             onClick={() => removeActivityImage(index, 'existing')}
                             className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full shadow-md hover:scale-110 transition-transform"
                           >
                             <X className="w-3 h-3" />
                           </button>
                           <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] font-bold text-center py-0.5">Existing</div>
                         </div>
                       ))}

                       {activityForm.newImages.map((file, index) => (
                         <div key={`new-${index}`} className="flex-shrink-0 w-24 h-24 relative rounded-xl overflow-hidden shadow-sm border border-emerald-200 group">
                           <img src={URL.createObjectURL(file)} alt="New" className="w-full h-full object-cover" />
                           <button 
                             onClick={() => removeActivityImage(index, 'new')}
                             className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full shadow-md hover:scale-110 transition-transform"
                           >
                             <X className="w-3 h-3" />
                           </button>
                           <div className="absolute bottom-0 inset-x-0 bg-emerald-500/80 text-white text-[9px] font-bold text-center py-0.5">New</div>
                         </div>
                       ))}
                    </div>
                    {(activityForm.existingImages.length + activityForm.newImages.length) === 0 && (
                      <p className="text-center text-xs text-slate-400 mt-2 font-medium">No images selected</p>
                    )}
                  </div>
               </div>
            </div>

            <div className="p-6 border-t flex gap-3 bg-slate-50/50 rounded-b-3xl">
              <button onClick={() => setIsActivityModalOpen(false)} className="flex-1 px-4 py-3 border rounded-xl font-bold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button onClick={handleActivitySubmit} disabled={isSubmittingActivity} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-700 shadow-lg shadow-blue-200 disabled:opacity-70">
                {isSubmittingActivity ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Activity
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: OUR CHAPTERS (NEW) --- */}
      {isChapterModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold text-slate-800">{editingChapter ? 'Edit Chapter' : 'Add New Chapter'}</h3>
              <button onClick={() => setIsChapterModalOpen(false)}><X className="w-5 h-5 text-slate-400 hover:text-slate-600" /></button>
            </div>
            
            <div className="p-6 space-y-6">
                
               {/* City & Active Status */}
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">City *</label>
                    <select 
                        value={chapterForm.city_id} 
                        onChange={(e) => setChapterForm({...chapterForm, city_id: e.target.value})}
                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-medium focus:ring-2 focus:ring-indigo-500 appearance-none"
                    >
                        <option value="">Select City</option>
                        {cities.map(city => (
                            <option key={city._id} value={city._id}>{city.name}</option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Status</label>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                        <span className={`text-sm font-bold ${chapterForm.isActive ? 'text-green-600' : 'text-slate-500'}`}>
                            {chapterForm.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button 
                            onClick={() => setChapterForm(prev => ({...prev, isActive: !prev.isActive}))}
                            className={`ml-auto w-12 h-6 rounded-full p-1 transition-colors ${chapterForm.isActive ? 'bg-green-500' : 'bg-slate-300'}`}
                        >
                            <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${chapterForm.isActive ? 'translate-x-6' : 'translate-x-0'}`} />
                        </button>
                    </div>
                  </div>
               </div>

               {/* Description */}
               <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Description *</label>
                  <textarea 
                    rows="2"
                    value={chapterForm.description} 
                    onChange={(e) => setChapterForm({...chapterForm, description: e.target.value})} 
                    placeholder="Short description about this chapter network..."
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-medium focus:ring-2 focus:ring-indigo-500" 
                  />
               </div>

               {/* Meeting Details */}
               <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-500"/> Meeting Schedule</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Day</label>
                            <select 
                                value={chapterForm.meeting.day}
                                onChange={(e) => setChapterForm(prev => ({...prev, meeting: {...prev.meeting, day: e.target.value}}))}
                                className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium outline-none focus:border-indigo-500"
                            >
                                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                    <option key={day} value={day}>{day}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Start Time</label>
                            <input 
                                type="text"
                                value={chapterForm.meeting.startTime}
                                onChange={(e) => setChapterForm(prev => ({...prev, meeting: {...prev.meeting, startTime: e.target.value}}))}
                                placeholder="e.g. 7:00 AM"
                                className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium outline-none focus:border-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">End Time</label>
                            <input 
                                type="text"
                                value={chapterForm.meeting.endTime}
                                onChange={(e) => setChapterForm(prev => ({...prev, meeting: {...prev.meeting, endTime: e.target.value}}))}
                                placeholder="e.g. 9:00 AM"
                                className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium outline-none focus:border-indigo-500"
                            />
                        </div>
                    </div>
               </div>

               {/* Highlights */}
               <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100">
                    <h4 className="text-sm font-bold text-amber-700 mb-3 flex items-center gap-2"><Briefcase className="w-4 h-4"/> Key Highlight</h4>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-[10px] font-bold text-amber-700/60 uppercase mb-1">Title</label>
                            <input 
                                type="text"
                                value={chapterForm.highlight.title}
                                onChange={(e) => setChapterForm(prev => ({...prev, highlight: {...prev.highlight, title: e.target.value}}))}
                                placeholder="e.g. Chapter Leaders"
                                className="w-full p-2.5 bg-white border border-amber-200 rounded-lg text-sm font-medium outline-none focus:border-amber-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-amber-700/60 uppercase mb-1">Description</label>
                            <textarea 
                                rows="2"
                                value={chapterForm.highlight.description}
                                onChange={(e) => setChapterForm(prev => ({...prev, highlight: {...prev.highlight, description: e.target.value}}))}
                                placeholder="Description of the highlight..."
                                className="w-full p-2.5 bg-white border border-amber-200 rounded-lg text-sm font-medium outline-none focus:border-amber-500"
                            />
                        </div>
                    </div>
               </div>
            </div>

            <div className="p-6 border-t flex gap-3 bg-slate-50/50 rounded-b-3xl">
              <button onClick={() => setIsChapterModalOpen(false)} className="flex-1 px-4 py-3 border rounded-xl font-bold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button onClick={handleChapterSubmit} disabled={isSubmittingChapter} className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 shadow-lg shadow-indigo-200 disabled:opacity-70">
                {isSubmittingChapter ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Chapter
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '', id: null })} />
      <ConfirmationModal isOpen={confirmation.isOpen} title={confirmation.title} message={confirmation.message} onConfirm={confirmation.onConfirm} onCancel={confirmation.onCancel} />
    </div>
  );
};

export default LandingPageManagement;